import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Container, Typography, Button, Grid, Card, CardContent,
  Stack, Divider, Avatar, Chip, useMediaQuery, useTheme
} from "@mui/material";
import {
  Storefront, LocalHospital, Speed, TrendingUp, Security,
  PhoneInTalk, Devices, CampaignOutlined, StarRate, RocketLaunch,
  CheckCircleOutline, ArrowForward, Groups, BarChart,
  Loyalty, DeliveryDining, SmartToy, LocalOffer
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const accent = "#0d47a1";
const accentLight = "#e3f2fd";
const accentDark = "#082e6a";
const grey50 = "#fafafa";
const grey100 = "#f5f5f5";

const stats = [
  { value: "16", label: "Functional Modules" },
  { value: "74", label: "Built-In Features" },
  { value: "5", label: "Payment Gateways" },
  { value: "0%", label: "Commission Fees" },
];

const industries = [
  {
    icon: <Storefront sx={{ fontSize: 48, color: accent }} />,
    title: "Restaurants",
    desc: "Direct online ordering, POS management, delivery logistics, loyalty programs, and customer acquisition — all zero commission. Coexist with Uber Eats, DoorDash & Just Eat while building your direct channel.",
    features: ["Online Ordering", "POS Terminal", "Kitchen Display", "Delivery & GPS Tracking", "Menu & Deals Engine", "Driver App"],
  },
  {
    icon: <LocalHospital sx={{ fontSize: 48, color: accent }} />,
    title: "Healthcare Providers",
    desc: "Online appointment booking, patient engagement, review management, telehealth integration, and AI-powered inbound call handling — all under your own brand.",
    features: ["Appointment Booking", "Telehealth (Zoom/Teams)", "Patient CRM", "Review Management", "Automated Reminders", "HIPAA Compliance"],
  },
];

const modules = [
  { icon: <RocketLaunch />, title: "Client Onboarding", desc: "Multi-step guided setup — go live in minutes, not months." },
  { icon: <Security />, title: "Multi-Payment Gateway", desc: "Stripe, Square, PayPal, Adyen & Worldpay — one unified layer." },
  { icon: <Devices />, title: "Own POS Hardware", desc: "Android POS terminal with KDS, real-time sync & end-of-day reports." },
  { icon: <LocalOffer />, title: "Menu & Deals Engine", desc: "Full menu control with modifiers, combos, specials & price floors." },
  { icon: <Storefront />, title: "Online Ordering & Booking", desc: "Zero-commission direct orders alongside aggregator integration." },
  { icon: <BarChart />, title: "Website Builder", desc: "Auto-generated branded website with SEO engine & GA4." },
  { icon: <Devices />, title: "Mobile App", desc: "White-label iOS & Android apps with push notifications." },
  { icon: <Loyalty />, title: "Loyalty & Rewards", desc: "Points, tiers, gift cards & automatic earn multipliers." },
  { icon: <CampaignOutlined />, title: "Marketing Automation", desc: "AI-generated campaigns — welcome, win-back, birthday & more." },
  { icon: <DeliveryDining />, title: "Driver App", desc: "Own delivery fleet management with QR-based direct-order conversion." },
  { icon: <TrendingUp />, title: "Customer Acquisition", desc: "Convert marketplace customers to direct with ROI dashboards." },
  { icon: <DeliveryDining />, title: "Delivery & Logistics", desc: "Zone config, own drivers or DoorDash Drive fallback, GPS tracking." },
  { icon: <SmartToy />, title: "AI Voice Agent", desc: "24/7 AI answers calls, takes orders, books appointments via ElevenLabs." },
  { icon: <Groups />, title: "CRM & Analytics", desc: "360° customer profiles with unified revenue dashboards." },
  { icon: <StarRate />, title: "Review Management", desc: "Automated review requests — 4-5★ to Google, lower captured privately." },
  { icon: <Security />, title: "Admin Panel", desc: "Super-admin architecture — add new industries with zero code changes." },
];

const howItWorks = [
  { step: "01", title: "Onboard in Minutes", desc: "Complete a guided form — business details, brand assets, payment gateway, AI voice config." },
  { step: "02", title: "Go Live Instantly", desc: "Your branded website, POS, and AI voice agent activate automatically." },
  { step: "03", title: "Receive Orders & Bookings", desc: "All channels (website, app, voice, Uber Eats) flow into one unified dashboard." },
  { step: "04", title: "Grow & Retain Customers", desc: "Loyalty, marketing automation, and review management work on autopilot." },
];

const testimonials = [
  {
    name: "Dr. Sarah Mitchell",
    role: "Dental Clinic Owner",
    text: "We cut no-shows by 40% with automated reminders and the AI voice agent handles after-hours calls perfectly. Patients love the seamless booking experience.",
  },
  {
    name: "Marco Rossi",
    role: "Restaurant Owner",
    text: "Switching from Uber Eats to direct ordering saved us thousands in commissions. The POS and kitchen display system keep our operations running smoothly.",
  },
  {
    name: "Priya Sharma",
    role: "Multi-Location Healthcare",
    text: "Having all patient data, bookings, and reviews in one place transformed how we manage our clinics. The ROI was visible from month one.",
  },
];

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <Helmet>
        <title>Rite Marketing Automation — Zero-Commission SaaS for Restaurants & Healthcare</title>
        <meta name="description" content="Rite is a fully integrated marketing automation platform for restaurants and healthcare providers. Direct ordering, AI voice agent, loyalty, CRM & analytics — all zero commission." />
        <meta name="keywords" content="marketing automation, restaurant ordering, healthcare booking, AI voice agent, POS system, loyalty program, SaaS platform" />
        <link rel="canonical" href="https://riteclinic.com" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Rite Marketing Automation Platform",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, iOS, Android",
          "description": "Zero-commission SaaS marketing automation for restaurants and healthcare providers.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "AUD" }
        })}</script>
      </Helmet>

      {/* ===== HERO ===== */}
      <Box sx={{
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 50%, #0a1929 100%)`,
        color: "#fff", py: { xs: 10, md: 16 }, position: "relative", overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", top: -100, right: -100, width: 400, height: 400,
          borderRadius: "50%", background: "rgba(255,255,255,0.03)",
        }} />
        <Box sx={{
          position: "absolute", bottom: -150, left: -150, width: 500, height: 500,
          borderRadius: "50%", background: "rgba(255,255,255,0.02)",
        }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip label="Trusted by Restaurants & Clinics" sx={{
                bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600,
                mb: 3, fontSize: "0.85rem", backdropFilter: "blur(4px)",
              }} />
              <Typography variant="h1" sx={{
                fontSize: { xs: "2.2rem", md: "3.4rem" }, fontWeight: 800,
                lineHeight: 1.15, mb: 3,
              }}>
                Own Your Customers.{" "}
                <Box component="span" sx={{ color: accentLight }}>
                  Own Your Revenue.
                </Box>
              </Typography>
              <Typography sx={{
                fontSize: { xs: "1rem", md: "1.2rem" }, lineHeight: 1.7,
                color: "rgba(255,255,255,0.8)", mb: 4, maxWidth: 560,
              }}>
                Rite is the all-in-one platform that replaces high-commission marketplaces
                with your own branded ordering, booking, loyalty, and AI voice system —
                built for restaurants and healthcare providers.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button variant="contained" size="large" component={Link} to="/client-signup"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: "#fff", color: accent, fontWeight: 700,
                    px: 4, py: 1.5, borderRadius: 2, fontSize: "1rem",
                    "&:hover": { bgcolor: accentLight, transform: "translateY(-2px)" },
                    transition: "all 0.3s",
                  }}
                >
                  Get Started Free
                </Button>
                <Button variant="outlined" size="large" component={Link} to="/services"
                  sx={{
                    borderColor: "rgba(255,255,255,0.4)", color: "#fff",
                    px: 4, py: 1.5, borderRadius: 2, fontSize: "1rem",
                    "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  Explore Features
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
              <Box sx={{
                width: 340, height: 340, borderRadius: "50%",
                background: `radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid rgba(255,255,255,0.1)",
              }}>
                <Stack spacing={1} alignItems="center">
                  <SmartToy sx={{ fontSize: 80, color: accentLight }} />
                  <Typography sx={{ color: accentLight, fontWeight: 700, fontSize: "1.1rem" }}>
                    AI-Powered Platform
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== STATS BAR ===== */}
      <Box sx={{ bgcolor: accentLight, py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Stack alignItems="center">
                  <Typography sx={{ fontSize: { xs: "2rem", md: "2.8rem" }, fontWeight: 800, color: accent }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ color: accentDark, fontWeight: 500, textAlign: "center" }}>
                    {s.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== INDUSTRIES ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.6rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            Built for Two Powerful Industries
          </Typography>
          <Typography textAlign="center" sx={{
            color: "#666", maxWidth: 650, mx: "auto", mb: 6, fontSize: "1.05rem",
          }}>
            Whether you run a restaurant or a healthcare practice, Rite gives you complete
            ownership of your customer relationships and revenue.
          </Typography>
          <Grid container spacing={4}>
            {industries.map((ind, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card elevation={0} sx={{
                  border: "1px solid #e0e0e0", borderRadius: 3, p: 3, height: "100%",
                  transition: "all 0.3s",
                  "&:hover": { borderColor: accent, boxShadow: `0 8px 30px ${accent}15`, transform: "translateY(-4px)" },
                }}>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{ind.icon}</Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mb: 1.5, fontSize: "1.5rem" }}>
                      {ind.title}
                    </Typography>
                    <Typography sx={{ color: "#555", mb: 3, lineHeight: 1.7 }}>
                      {ind.desc}
                    </Typography>
                    <Grid container spacing={1}>
                      {ind.features.map((f, j) => (
                        <Grid item xs={6} key={j}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleOutline sx={{ fontSize: 18, color: accent }} />
                            <Typography sx={{ fontSize: "0.85rem", color: "#444" }}>{f}</Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== HOW IT WORKS ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: grey50 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.6rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            How It Works
          </Typography>
          <Typography textAlign="center" sx={{ color: "#666", maxWidth: 550, mx: "auto", mb: 6 }}>
            From sign-up to fully operational — in four simple steps.
          </Typography>
          <Grid container spacing={4}>
            {howItWorks.map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ textAlign: "center" }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: "50%", bgcolor: accentLight,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    mx: "auto", mb: 2,
                  }}>
                    <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: accent }}>
                      {item.step}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: accent, mb: 1, fontSize: "1.1rem" }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== MODULES OVERVIEW ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.6rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            16 Modules. One Unified Platform.
          </Typography>
          <Typography textAlign="center" sx={{ color: "#666", maxWidth: 600, mx: "auto", mb: 6 }}>
            Every module works together seamlessly — shared customer profiles, unified payments,
            and a single analytics engine.
          </Typography>
          <Grid container spacing={3}>
            {modules.map((m, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card elevation={0} sx={{
                  border: "1px solid #eee", borderRadius: 2, p: 2.5, height: "100%",
                  transition: "all 0.3s",
                  "&:hover": { bgcolor: accentLight, borderColor: accent, transform: "translateY(-3px)" },
                }}>
                  <CardContent sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: accentLight, color: accent, mb: 1.5, width: 44, height: 44 }}>
                      {m.icon}
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, color: "#222", mb: 0.5, fontSize: "0.95rem" }}>
                      {m.title}
                    </Typography>
                    <Typography sx={{ color: "#777", fontSize: "0.82rem", lineHeight: 1.5 }}>
                      {m.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box textAlign="center" mt={5}>
            <Button variant="outlined" component={Link} to="/services" endIcon={<ArrowForward />}
              sx={{
                borderColor: accent, color: accent, px: 4, py: 1.2, borderRadius: 2,
                fontWeight: 600, "&:hover": { bgcolor: accentLight },
              }}
            >
              View All Features in Detail
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== TESTIMONIALS ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: grey100 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.6rem" }, fontWeight: 800, color: accent, mb: 6,
          }}>
            What Our Clients Say
          </Typography>
          <Grid container spacing={4}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card elevation={0} sx={{
                  border: "1px solid #e0e0e0", borderRadius: 3, p: 3, height: "100%",
                  position: "relative", overflow: "visible",
                }}>
                  <CardContent>
                    <Typography sx={{ fontSize: "2.5rem", color: accent, fontWeight: 800, lineHeight: 1, mb: 1 }}>
                      "
                    </Typography>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mb: 3, fontStyle: "italic" }}>
                      {t.text}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontWeight: 700, color: "#222" }}>{t.name}</Typography>
                    <Typography sx={{ color: "#999", fontSize: "0.85rem" }}>{t.role}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== AI VOICE HIGHLIGHT ===== */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff",
      }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <PhoneInTalk sx={{ fontSize: 56, mb: 2, color: accentLight }} />
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "1.6rem", md: "2.2rem" } }}>
            AI Voice Agent — Your 24/7 Virtual Receptionist
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1.05rem", lineHeight: 1.7, mb: 4, maxWidth: 600, mx: "auto" }}>
            Powered by ElevenLabs & ChatGPT, your AI voice agent answers calls, takes orders,
            books appointments, checks order status, and escalates to staff — all with natural
            human-like conversation. Full call recording, transcription & analytics included.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            {["Takes Orders", "Books Appointments", "Answers FAQs", "Escalates to Staff"].map((f) => (
              <Chip key={f} label={f} sx={{
                bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600,
                fontSize: "0.85rem", mb: 1,
              }} />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ===== FINAL CTA ===== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "#fff", textAlign: "center" }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, color: accent, mb: 2, fontSize: { xs: "1.6rem", md: "2.2rem" } }}>
            Ready to Take Control?
          </Typography>
          <Typography sx={{ color: "#666", mb: 4, lineHeight: 1.7 }}>
            Join restaurants and healthcare providers who have eliminated marketplace
            commissions and own 100% of their customer relationships.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" component={Link} to="/client-signup"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: accent, px: 5, py: 1.5, borderRadius: 2, fontWeight: 700,
                fontSize: "1rem",
                "&:hover": { bgcolor: accentDark, transform: "translateY(-2px)" },
                transition: "all 0.3s",
              }}
            >
              Start Free Trial
            </Button>
            <Button variant="outlined" size="large" component={Link} to="/about"
              sx={{
                borderColor: accent, color: accent, px: 4, py: 1.5, borderRadius: 2,
                fontWeight: 600, "&:hover": { bgcolor: accentLight },
              }}
            >
              Learn About Us
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
